// ============================================================================
// CHANGO V5.0 — Login Page (T-D1-019)
// ============================================================================
// Plan §3.1 — email + password login, with optional 2FA step-up when the
// backend returns { two_factor_required: true, challenge }.
// react-hook-form + zod for validation. The form's submit handler calls
// useAuth().login(...) and handles the two branching outcomes.
// ============================================================================

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useAuth } from '../../hooks/useAuth.js';
import { HttpError } from '../../api/client.js';

const schema = z.object({
  email: z.string().trim().email('请输入有效的邮箱地址'),
  password: z.string().min(1, '请输入密码'),
  remember: z.boolean().optional()
});

export default function Login() {
  const { login, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/dashboard';

  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onTouched',
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '', remember: false }
  });

  const onSubmit = async (values) => {
    setServerError(null);
    clearError();
    setSubmitting(true);
    try {
      const res = await login(values.email, values.password);
      if (res?.twoFactor?.challenge) {
        // Step-up to 2FA page carrying the challenge token.
        navigate('/2fa', {
          state: { challenge: res.twoFactor.challenge, from }
        });
        return;
      }
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof HttpError) {
        if (err.status === 429) {
          setServerError('登录尝试过于频繁，账号已临时锁定，请稍后重试。');
        } else if (err.code === 'BAD_CREDENTIALS' || err.code === 'UNAUTHORIZED') {
          setServerError('邮箱或密码错误');
        } else {
          setServerError(err.message || '登录失败');
        }
      } else {
        setServerError('网络错误，请重试');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <aside className="hidden w-1/2 flex-col justify-between bg-gradient-to-br from-chango-700 to-chango-900 p-12 text-white lg:flex">
        <div>
          <h1 className="text-3xl font-semibold">长戈 · CHANGO</h1>
          <p className="mt-2 text-chango-100">出口决策云 V5.0</p>
        </div>
        <div>
          <p className="text-2xl font-light leading-relaxed">
            让每一个报价
            <br />都经得起决策。
          </p>
          <p className="mt-6 text-sm text-chango-200">© 2026 长戈（CHANGO）</p>
        </div>
      </aside>

      <main className="flex flex-1 items-center justify-center bg-slate-50 p-8">
        <div className="card w-full max-w-md p-8">
          <h2 className="mb-6 text-xl font-semibold text-slate-800">登录</h2>

          {serverError ? (
            <div role="alert" className="alert-error mb-4">{serverError}</div>
          ) : null}

          <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="label" htmlFor="email">邮箱</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                className={`field ${errors.email ? 'field-error' : ''}`}
                {...register('email')}
              />
              {errors.email ? <p className="err">{errors.email.message}</p> : null}
            </div>

            <div>
              <label className="label" htmlFor="password">密码</label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                className={`field ${errors.password ? 'field-error' : ''}`}
                {...register('password')}
              />
              {errors.password ? <p className="err">{errors.password.message}</p> : null}
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="inline-flex items-center gap-2 text-slate-600">
                <input type="checkbox" {...register('remember')} /> 记住我
              </label>
              <Link to="/forgot-password" className="text-chango-600 hover:underline">忘记密码?</Link>
            </div>

            <button
              type="submit"
              disabled={submitting || !isValid}
              className="btn-primary w-full"
            >
              {submitting ? <span className="spinner" aria-hidden="true" /> : null}
              {submitting ? '登录中…' : '登录'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            还没有账号？
            <Link to="/register" className="ml-1 font-medium text-chango-600 hover:underline">我要注册账号</Link>
          </p>
        </div>
      </main>
    </div>
  );
}
